import React, { useState, useEffect } from 'react';
import {
    Button,
    Box,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Dialog,
    DialogContent,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Typography
} from '@mui/material';
import {
    Delete as DeleteIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import MarkingService from '../services/MarkingService';
import { parseMarkingCode } from '../utils/markingCodeParser';
import { useTranslation } from 'react-i18next';

const MarkingCodeModal = ({ isOpen, onClose, demand, product, onComplete }) => {
    const { t } = useTranslation();
    const [scannedCodes, setScannedCodes] = useState([]);
    const [codeValidations, setCodeValidations] = useState({}); // Хранит результаты проверки для каждого кода
    const [currentInput, setCurrentInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidatingCode, setIsValidatingCode] = useState(false);
    const [lastValidationMessage, setLastValidationMessage] = useState('');
    const [lastMessageType, setLastMessageType] = useState(''); // 'success', 'warning', 'error', 'info'

    // Получение статуса кода для визуализации
    const getCodeStatus = (code) => {
        const validation = codeValidations[code];

        if (!validation) {
            return { status: 'unknown', color: 'default', icon: null, text: t('chestnyZnak.checking') };
        }

        if (validation.source === 'error') {
            return { status: 'error', color: 'error', icon: <ErrorIcon />, text: t('chestnyZnak.checkError') };
        }

        if (validation.source === 'local' && validation.used) {
            return { status: 'duplicate', color: 'error', icon: <ErrorIcon />, text: t('marking.codeAlreadyChecked') };
        }

        if (validation.source === 'chestnyznak') {
            // Проверяем все возможные комбинации полей для определения валидности
            const isValid = validation.valid || validation.isValid;
            const isOwnedByFLX = validation.isOwnedByFLX;
            const isAvailableForSale = validation.isAvailableForSale;

            // Если код действителен и доступен для продажи, считаем его валидным
            if (isValid && isAvailableForSale) {
                return { status: 'valid', color: 'success', icon: <CheckIcon />, text: t('chestnyZnak.valid') };
            } else {
                // Все остальные случаи - код существует, но недоступен
                return { status: 'unavailable', color: 'warning', icon: <WarningIcon />, text: t('marking.unavailable') };
            }
        }

        return { status: 'invalid', color: 'error', icon: <ErrorIcon />, text: t('chestnyZnak.invalid') };
    };

    // Проверка, является ли код валидным для добавления
    const isCodeValidForAddition = (code) => {
        const status = getCodeStatus(code);
        return status.status === 'valid';
    };

    // Получение количества валидных кодов
    const getValidCodesCount = () => {
        return scannedCodes.filter(code => isCodeValidForAddition(code)).length;
    };

    // Обработка сканирования DataMatrix кодов
    useEffect(() => {
        if (!isOpen) return;

        async function handleKeyDown(e) {
            // Останавливаем всплытие события для всех клавиш когда модальное окно открыто
            e.stopPropagation();
            
            if (e.key === 'Enter' && currentInput.trim().length > 0) {
                e.preventDefault(); // Предотвращаем стандартное поведение Enter
                const originalCode = currentInput.trim();

                // Парсим код до стандартного формата (31 символ)
                const parsedCode = parseMarkingCode(originalCode);
                console.log(`🔧 Обработка кода: ${originalCode} -> ${parsedCode}`);

                // Проверка лимита
                if (scannedCodes.length >= product.quantity) {
                    setLastValidationMessage(t('marking.maxCodesReached', { count: product.quantity }));
                    setLastMessageType('warning');
                    setCurrentInput('');
                    return;
                }

                // Проверка локальных дубликатов (в рамках данной позиции) - используем обрезанный код
                if (scannedCodes.includes(parsedCode)) {
                    setLastValidationMessage(t('marking.codeAlreadyScanned', { code: parsedCode }));
                    setLastMessageType('warning');
                    setCurrentInput('');
                    return;
                }

                // Проверяем, не добавлен ли уже код с результатами проверки
                if (codeValidations[parsedCode]) {
                    setLastValidationMessage(t('marking.codeAlreadyChecked'));
                    setLastMessageType('warning');
                    setCurrentInput('');
                    return;
                }

                // Проверка через ChestnyZnak API
                setIsValidatingCode(true);
                setLastValidationMessage('');

                try {
                    const validationResult = await MarkingService.quickCodeCheck(parsedCode);

                    // Вычисляем статус напрямую из validationResult, а не из состояния
                    let status;
                    if (validationResult.source === 'chestnyznak') {
                        const isValid = validationResult.valid || validationResult.isValid;
                        const isAvailableForSale = validationResult.isAvailableForSale;

                        if (isValid && isAvailableForSale) {
                            status = { status: 'valid', color: 'success', icon: <CheckIcon />, text: t('chestnyZnak.valid') };
                        } else {
                            status = { status: 'unavailable', color: 'warning', icon: <WarningIcon />, text: t('marking.unavailable') };
                        }
                    } else if (validationResult.source === 'error') {
                        status = { status: 'error', color: 'error', icon: <ErrorIcon />, text: t('chestnyZnak.checkError') };
                    } else if (validationResult.source === 'local' && validationResult.used) {
                        status = { status: 'duplicate', color: 'error', icon: <ErrorIcon />, text: t('marking.codeAlreadyChecked') };
                    } else {
                        status = { status: 'invalid', color: 'error', icon: <ErrorIcon />, text: t('chestnyZnak.invalid') };
                    }

                    if (status.status === 'valid') {
                        // Код валидный и принадлежит FLX - добавляем обрезанный код
                        setScannedCodes(prev => [...prev, parsedCode]);
                        setLastValidationMessage(t('marking.codeSuccessfullyAdded'));
                        setLastMessageType('success');
                    } else {
                        // Код невалидный или не принадлежит FLX
                        let message = '';
                        let messageType = 'warning'; // по умолчанию warning

                        switch (status.status) {
                            case 'unavailable':
                                if (validationResult.source === 'local' && validationResult.used) {
                                    message = t('marking.codeAlreadyInUse');
                                } else {
                                    message = t('marking.codeNotAvailable');
                                }
                                messageType = 'warning';
                                break;
                            case 'duplicate':
                                message = t('marking.codeDuplicate', { code: parsedCode });
                                messageType = 'warning';
                                break;
                            case 'error':
                                message = t('marking.codeCheckError', { message: validationResult.message });
                                messageType = 'error';
                                break;
                            default:
                                // Исправляем некорректное сообщение - проверяем действительность кода
                                if (validationResult.valid && validationResult.message) {
                                    message = validationResult.message;
                                    messageType = 'warning';
                                } else {
                                    message = t('marking.codeInvalidMsg', { message: validationResult.message ? ': ' + validationResult.message : '' });
                                    messageType = 'error';
                                }
                        }
                        setLastValidationMessage(message);
                        setLastMessageType(messageType);
                    }

                    // Сохраняем результат проверки с использованием обрезанного кода (после вычисления статуса)
                    setCodeValidations(prev => ({
                        ...prev,
                        [parsedCode]: validationResult
                    }));

                } catch (error) {
                    console.error('Error validating code:', error);
                    setLastValidationMessage(t('marking.codeCheckError', { message: error.message }));
                    setLastMessageType('error');

                    // Сохраняем результат ошибки с использованием обрезанного кода
                    setCodeValidations(prev => ({
                        ...prev,
                        [parsedCode]: {
                            source: 'error',
                            valid: false,
                            message: error.message
                        }
                    }));
                } finally {
                    setIsValidatingCode(false);
                    setCurrentInput('');
                }
                return;
            }

            // Накопление символов (исключаем служебные клавиши)
            if (e.key !== 'Enter' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                e.preventDefault(); // Блокируем браузерные горячие клавиши (/, Ctrl+F и т.д.)
                setCurrentInput(prev => prev + e.key);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentInput, scannedCodes, product?.quantity]);

    // Удаление кода
    const handleDeleteCode = (codeToDelete) => {
        setScannedCodes(prev => prev.filter(code => code !== codeToDelete));
        // Также удаляем результат проверки
        setCodeValidations(prev => {
            const newValidations = { ...prev };
            delete newValidations[codeToDelete];
            return newValidations;
        });
        setLastValidationMessage(t('marking.codeDeleted'));
        setLastMessageType('info');
    };

    // Завершение сканирования
    const handleComplete = async () => {
        const validCodesCount = getValidCodesCount();

        if (validCodesCount !== product.quantity) {
            setLastValidationMessage(t('marking.needValidCodes', { required: product.quantity, added: validCodesCount }));
            setLastMessageType('warning');
            return;
        }

        // Проверяем, все ли коды валидны
        const invalidCodes = scannedCodes.filter(code => !isCodeValidForAddition(code));
        if (invalidCodes.length > 0) {
            setLastValidationMessage(t('marking.invalidCodesPresent'));
            setLastMessageType('error');
            return;
        }

        setIsSubmitting(true);
        try {
            await MarkingService.sendMarkingCodesToMoySklad(
                demand.id,
                product.positionId,
                scannedCodes
            );

            setLastValidationMessage(t('marking.successfullySent', { count: scannedCodes.length }));
            setLastMessageType('success');
            onComplete(product);
        } catch (error) {
            console.error('Marking codes error:', error);
            setLastValidationMessage(t('marking.sendError', { error: error.response?.data?.error || error.message }));
            setLastMessageType('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Сброс состояния при закрытии
    useEffect(() => {
        if (!isOpen) {
            setScannedCodes([]);
            setCodeValidations({});
            setCurrentInput('');
            setLastValidationMessage('');
            setLastMessageType('');
            setIsValidatingCode(false);
        }
    }, [isOpen]);

    // Консольные функции для тестирования сканирования кодов
    useEffect(() => {
        if (isOpen) {
            // Добавляем глобальную функцию для тестирования одного кода
            window.testScanCode = (testCode) => {
                if (!testCode) {
                    console.log('❌ Укажите код для тестирования: window.testScanCode("ваш_код")');
                    return;
                }

                console.log(`🔍 Тестирование кода: ${testCode}`);

                // Симулируем ввод кода
                setCurrentInput(testCode);

                // Небольшая задержка для визуализации
                setTimeout(() => {
                    // Симулируем нажатие Enter
                    const event = new KeyboardEvent('keydown', {
                        key: 'Enter',
                        code: 'Enter',
                        keyCode: 13,
                        which: 13,
                        bubbles: true
                    });
                    document.dispatchEvent(event);
                }, 100);
            };

            // Добавляем функцию для массового тестирования
            window.testMultipleCodes = (codes) => {
                if (!Array.isArray(codes)) {
                    console.log('❌ Укажите массив кодов: window.testMultipleCodes(["код1", "код2", "код3"])');
                    return;
                }

                console.log(`🔍 Начинаю тестирование ${codes.length} кодов...`);

                codes.forEach((code, index) => {
                    setTimeout(() => {
                        window.testScanCode(code);
                    }, index * 1000); // Интервал 1 секунда между кодами
                });
            };

            // Добавляем функцию для получения тестовых кодов
            window.getTestCodes = () => {
                return [
                    '010463004277800321O6v+K8vXF4uEEc6fhDuRVd8uInj2lM0xB5', // Пример валидного кода
                    '010463004277800321O6v+K8vXF4uEEc6fhDuRVd8uInj2lM0xB6', // Пример кода не принадлежащего FLX
                    'invalid_code_format', // Пример невалидного кода
                ];
            };

            // Добавляем функцию для очистки всех кодов
            window.clearTestCodes = () => {
                setScannedCodes([]);
                setCodeValidations({});
                setLastValidationMessage('Все тестовые коды очищены');
                setLastMessageType('info');
                console.log('🗑️ Все тестовые коды очищены');
            };

            // Добавляем функцию для получения текущего состояния
            window.getTestState = () => {
                const validCodesCount = scannedCodes.filter(code => {
                    const status = getCodeStatus(code);
                    return status.status === 'valid';
                }).length;

                return {
                    scannedCodes: scannedCodes.length,
                    validCodes: validCodesCount,
                    requiredCodes: product?.quantity || 0,
                    isComplete: validCodesCount === (product?.quantity || 0),
                    codeValidations: Object.keys(codeValidations).length
                };
            };

            // Информация о доступных функциях
            console.log('🛠️ Доступные функции для тестирования:');
            console.log('  window.testScanCode("код") - протестировать один код');
            console.log('  window.testMultipleCodes(["код1","код2"]) - протестировать несколько кодов');
            console.log('  window.getTestCodes() - получить примеры тестовых кодов');
            console.log('  window.clearTestCodes() - очистить все тестовые коды');
            console.log('  window.getTestState() - получить текущее состояние тестирования');

            // Очистка при закрытии модального окна
            return () => {
                delete window.testScanCode;
                delete window.testMultipleCodes;
                delete window.getTestCodes;
                delete window.clearTestCodes;
                delete window.getTestState;
            };
        }
    }, [isOpen, scannedCodes, codeValidations, product?.quantity, getCodeStatus]);

    // Проверяем наличие product
    if (!product) {
        return null;
    }

    const validCodesCount = getValidCodesCount();
    const isComplete = validCodesCount === product.quantity;

    return (
        <Dialog 
            open={isOpen} 
            onClose={() => {}} // Отключаем стандартное закрытие
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown={true}
        >
            <DialogContent>
                <Box sx={{ p: 2, minWidth: 500, maxWidth: 700 }}>
                <h5 style={{ color: '#1976d2', marginBottom: '1rem' }}>
                    🏷️ {t('marking.title')}
                </h5>

                <h6 style={{ marginBottom: '1rem' }}>
                    {product.name}
                </h6>

                <div style={{ marginBottom: '1rem' }}>
                    {t('label.article')}: <strong>{product.article}</strong>
                </div>

                <h6 style={{
                    color: isComplete ? '#2e7d32' : '#ed6c02',
                    fontWeight: 'bold',
                    marginBottom: '1rem'
                }}>
                    {t('marking.validCodes', { valid: validCodesCount, total: product.quantity })}
                </h6>

                {/* Индикатор прогресса */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <span style={{ fontSize: '14px', color: '#666' }}>
                            {t('marking.progress')}: {Math.round((validCodesCount / product.quantity) * 100)}%
                        </span>
                        <span style={{ fontSize: '14px', color: '#666' }}>
                            {t('marking.totalScanned')}: {scannedCodes.length}
                        </span>
                    </Box>
                    <Box sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            width: `${(validCodesCount / product.quantity) * 100}%`,
                            height: '100%',
                            backgroundColor: isComplete ? '#4caf50' : '#ff9800',
                            transition: 'width 0.3s ease'
                        }} />
                    </Box>
                </Box>

                <div style={{ fontSize: '14px', color: '#666', marginBottom: '1rem' }}>
                    {t('marking.scanDataMatrix')}
                </div>

                {/* Индикатор загрузки при проверке кода */}
                {isValidatingCode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#1976d2' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <span>{t('marking.checkingCode')}</span>
                    </Box>
                )}

                {/* Сообщение о результате проверки */}
                {lastValidationMessage && (
                    <Alert
                        severity={lastMessageType || 'info'}
                        sx={{ mb: 2 }}
                        onClose={() => {
                            setLastValidationMessage('');
                            setLastMessageType('');
                        }}
                    >
                        {lastValidationMessage}
                    </Alert>
                )}

                {/* Список отсканированных кодов */}
                {scannedCodes.length > 0 && (
                    <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <List dense>
                            {scannedCodes.map((code, index) => {
                                const status = getCodeStatus(code);
                                const validation = codeValidations[code];
                                const isValid = isCodeValidForAddition(code);

                                return (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            borderLeft: 4,
                                            borderColor: status.color === 'success' ? '#4caf50' :
                                                         status.color === 'warning' ? '#ff9800' :
                                                         status.color === 'error' ? '#f44336' : '#e0e0e0',
                                            backgroundColor: isValid ? '#f8fff8' : '#fff8f8',
                                            mb: 1,
                                            borderRadius: 1
                                        }}
                                        secondaryAction={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* Статус иконка */}
                                                {status.icon && (
                                                    <Tooltip title={status.text}>
                                                        <Box sx={{ color: status.color === 'success' ? '#4caf50' :
                                                                     status.color === 'warning' ? '#ff9800' :
                                                                     status.color === 'error' ? '#f44336' : '#9e9e9e' }}>
                                                            {status.icon}
                                                        </Box>
                                                    </Tooltip>
                                                )}

                                                {/* Кнопка удаления */}
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleDeleteCode(code)}
                                                    size="small"
                                                    sx={{ color: '#666' }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                        {index + 1}. {code}
                                                    </span>
                                                    <Chip
                                                        label={status.text}
                                                        size="small"
                                                        color={status.color}
                                                        variant={isValid ? 'filled' : 'outlined'}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                validation && validation.cisInfo && (
                                                    <Box sx={{ mt: 0.5, fontSize: '12px', color: '#666' }}>
                                                        {validation.cisInfo.productName && (
                                                            <div>🏷️ {validation.cisInfo.productName}</div>
                                                        )}
                                                        {validation.cisInfo.ownerName && (
                                                            <div>👤 {validation.cisInfo.ownerName}</div>
                                                        )}
                                                        {validation.cisInfo.gtin && (
                                                            <div>📊 GTIN: {validation.cisInfo.gtin}</div>
                                                        )}
                                                        {validation.source === 'error' && validation.message && (
                                                            <div style={{ color: '#f44336' }}>
                                                                ❌ {validation.message}
                                                            </div>
                                                        )}
                                                    </Box>
                                                )
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                )}

                {/* Информационная панель с легендой */}
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        📋 {t('marking.statusesTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            icon={<CheckIcon />}
                            label={t('marking.validFLX')}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                        <Chip
                            icon={<WarningIcon />}
                            label={t('marking.unavailable')}
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                        <Chip
                            icon={<ErrorIcon />}
                            label={t('marking.errorNotFound')}
                            size="small"
                            color="error"
                            variant="outlined"
                        />
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
                        💡 {t('marking.autoTruncate')}
                    </Typography>
                </Box>

                {/* Текущий ввод для отладки */}
                {currentInput && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '1rem' }}>
                        {t('marking.currentInput')}: {currentInput}
                    </div>
                )}

                {/* Кнопки управления */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('btn.cancel')}
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleComplete}
                        disabled={!isComplete || isSubmitting}
                    >
                        {isSubmitting ? t('marking.sending') : t('marking.complete')}
                    </Button>
                </Box>
            </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MarkingCodeModal;